import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  TreeDeciduous,
  LayoutDashboard,
  FileText,
  Users,
  TreePine,
  MessageSquare,
  Settings,
  User,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Compass,
  Shield
} from 'lucide-react';

const navItemsWaldbesitzer = [
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard, fallback: 'Dashboard' },
  { path: '/fallanalyse', labelKey: 'nav.caseAnalysis', icon: Compass, fallback: 'Fallanalyse' },
  { path: '/projekte', labelKey: 'nav.projects', icon: FileText, fallback: 'Projekte' },
  { path: '/anbieter', labelKey: 'nav.providers', icon: Users, fallback: 'Anbieter' },
  { path: '/mein-wald', labelKey: 'nav.myForest', icon: TreePine, fallback: 'Mein Wald' },
  { path: '/nachrichten', labelKey: 'nav.messages', icon: MessageSquare, fallback: 'Nachrichten' },
];

const navItemsAnbieter = [
  { path: '/projekte', labelKey: 'nav.projects', icon: FileText, fallback: 'Projekte' },
  { path: '/anbieter', labelKey: 'nav.providers', icon: Users, fallback: 'Anbieter' },
];

const navItemAdmin = { path: '/admin', labelKey: null, icon: Shield, fallback: 'Administration' };

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isWaldbesitzer = user?.role === 'waldbesitzer';
  const isAdmin = user?.role === 'admin';
  const baseItems = isAdmin ? navItemsWaldbesitzer : (isWaldbesitzer ? navItemsWaldbesitzer : navItemsAnbieter);
  const navItems = isAdmin ? [...baseItems, navItemAdmin] : baseItems;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-warm-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-warm-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="appshell-logo">
              <div className="p-1.5 rounded-lg bg-primary">
                <TreeDeciduous className="h-5 w-5 text-white" />
              </div>
              <span className="font-serif text-lg font-bold text-primary">ForestHub</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary/10 text-primary'
                      : 'text-gray-600 hover:bg-warm-100 hover:text-gray-900'
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.labelKey ? (t(item.labelKey) !== item.labelKey ? t(item.labelKey) : item.fallback) : item.fallback}
                </Link>
              ))}
            </nav>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <span className="hidden sm:inline text-sm font-medium text-gray-700">
                      {user?.name?.split(' ')[0]}
                    </span>
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                    <p className="text-xs text-primary mt-1">
                      {isWaldbesitzer ? t('appShell.rolleWaldbesitzer') : t('appShell.rolleDienstleister')}
                    </p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profil" className="flex items-center gap-2 cursor-pointer">
                      <User className="h-4 w-4" />
                      {t('nav.profile')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/einstellungen" className="flex items-center gap-2 cursor-pointer">
                      <Settings className="h-4 w-4" />
                      {t('nav.settings')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="flex items-center gap-2 cursor-pointer text-red-600"
                  >
                    <LogOut className="h-4 w-4" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-warm-200 bg-white">
            <nav className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary/10 text-primary'
                      : 'text-gray-600 hover:bg-warm-100'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.labelKey ? (t(item.labelKey) !== item.labelKey ? t(item.labelKey) : item.fallback) : item.fallback}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AppShell;
